import React from "react";
import {Button, styled, Text} from "@reside/ui";

import {WorkflowOption} from "./types";
import {Spinner} from "../spinner";
import {useFacilityWorkflowsQuery} from "./hooks/useFacilityWorkflowsQuery";

type Props = Readonly<{
  /** When set, loads workflows for that facility. */
  facilityId?: string;
  onSelect: (workflow: WorkflowOption) => void;
}>;

export const WorkflowList = ({facilityId, onSelect}: Props) => {
  const {data: facilityWorkflows, isLoading: isLoadingWorkflows} =
    useFacilityWorkflowsQuery(facilityId);

  const workflows = facilityWorkflows?.workflows ?? [];

  if (isLoadingWorkflows) {
    return (
      <FieldWrapper>
        <FieldLabel>Select workflow:</FieldLabel>
        <SpinnerWrapper>
          <Spinner relative />
        </SpinnerWrapper>
      </FieldWrapper>
    );
  }

  if (workflows.length === 0) {
    return (
      <FieldWrapper>
        <FieldLabel>Select workflow:</FieldLabel>
        <LoadingMessage>
          No workflows available for this facility.
        </LoadingMessage>
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper>
      <FieldLabel>Select workflow:</FieldLabel>
      <List>
        {workflows.map(workflow => (
          <WorkflowCard
            key={workflow.type}
            data-test-id={`startWorkflowOption-${workflow.type}`}
          >
            <WorkflowLabel>{workflow.label}</WorkflowLabel>
            <Button
              color="primary"
              testId={`startWorkflowStartButton-${workflow.type}`}
              onClick={() => onSelect(workflow)}
            >
              Start
            </Button>
          </WorkflowCard>
        ))}
      </List>
    </FieldWrapper>
  );
};

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({theme}) => theme.space[4]}px;
  color: ${({theme}) => theme.color.darkBlue100};
`;

const FieldLabel = styled(Text)`
  font-size: 18px;
  font-weight: 500;
`;

const SpinnerWrapper = styled.div`
  margin: auto;
  display: block;
  top: 10px;
  height: 0;
`;

const LoadingMessage = styled(Text)`
  font-size: 14px;
  color: ${({theme}) => theme.color.gray100};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({theme}) => theme.space[3]}px;
`;

const WorkflowCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({theme}) => theme.space[4]}px;
  border: 1px solid ${({theme}) => theme.color.borderGray};
  border-radius: 8px;
  padding: ${({theme}) => theme.space[4]}px ${({theme}) => theme.space[5]}px;
`;

const WorkflowLabel = styled(Text)`
  font-size: 16px;
  font-weight: 500;
`;
